/**
 * 掲示板の宛先ピッカー (v3.5 Board / Phase 2)。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-6
 *
 * - listAdminDirectory の active 内部メンバーをチェックボックスリスト式ポップオーバーで表示
 * - 自分自身は除外 (サーバ側でも silently drop されるが UI にも出さない)
 * - 未選択 = 全員宛 (ステータス追跡なし) — フィールド下に注意文を表示
 * - チェックボックスはネイティブ input のため Space/Enter でのキーボード操作に対応
 */
import { ChevronDown, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import { useAuth } from '@/hooks/useAuth';

interface RecipientPickerProps {
  /** 選択中の宛先 uid。 */
  value: string[];
  onChange: (uids: string[]) => void;
}

export function RecipientPicker({ value, onChange }: RecipientPickerProps) {
  const { user } = useAuth();
  const directory = useAdminDirectory();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 自分自身を除外した内部メンバー一覧
  const members = useMemo(
    () => (directory.data ?? []).filter((m) => m.uid !== user?.uid),
    [directory.data, user?.uid],
  );

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of directory.data ?? []) {
      map.set(m.uid, m.fullName || m.displayName || m.uid);
    }
    return (uid: string) => map.get(uid) ?? uid;
  }, [directory.data]);

  // ポップオーバー外クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleUid = (uid: string) => {
    onChange(value.includes(uid) ? value.filter((v) => v !== uid) : [...value, uid]);
  };

  return (
    <div>
      <div className="relative max-w-md" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="w-full min-h-9 rounded-sm border border-[var(--border)] px-2 py-1.5 text-left flex items-center gap-1.5 flex-wrap hover:border-[var(--text-muted)] transition-colors focus-visible:outline-2 focus-visible:outline-[var(--border-focus)]"
        >
          <Users className="size-4 text-[var(--text-muted)] flex-shrink-0" />
          {value.length === 0 ? (
            <span className="text-xs text-[var(--text-muted)]">全員宛（未選択）</span>
          ) : (
            value.map((uid) => (
              <span
                key={uid}
                className="inline-flex items-center rounded-full bg-[var(--accent-subtle)] text-[var(--accent)] px-2 py-0.5 text-[11px] font-medium"
              >
                {nameOf(uid)}
              </span>
            ))
          )}
          <ChevronDown className="size-4 ml-auto text-[var(--text-muted)] flex-shrink-0" />
        </button>
        {/* ※背景は必ず実在トークン (--bg-surface / --bg-elevated / --bg-input) を使う。
            --bg-card は globals.css に一度も定義されておらず、透明化してフォーム下の
            本文エディタが透けるバグの原因だった (2026-07-25 修正)。
            2026-08-21 に残り 78 箇所を一掃し、--bg-card は全廃 (カード面は spec §8.3 の
            --bg-surface が正)。再導入しないこと。 */}
        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg max-h-64 overflow-y-auto p-1">
            {directory.isLoading ? (
              <p className="px-2 py-2 text-xs text-[var(--text-muted)]">読込中...</p>
            ) : members.length === 0 ? (
              <p className="px-2 py-2 text-xs text-[var(--text-muted)]">
                選択できるメンバーがいません
              </p>
            ) : (
              members.map((m) => (
                <label
                  key={m.uid}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-[var(--bg-hover)] cursor-pointer text-[13px] text-[var(--text-primary)]"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(m.uid)}
                    onChange={() => toggleUid(m.uid)}
                  />
                  <span className="truncate">{m.fullName || m.displayName || m.uid}</span>
                  {m.dept && (
                    <span className="text-[11px] text-[var(--text-muted)] flex-shrink-0">
                      {m.dept}
                    </span>
                  )}
                </label>
              ))
            )}
          </div>
        )}
      </div>
      {value.length > 0 ? (
        <p className="text-xs text-[var(--text-muted)] mt-1.5">
          {value.length}名に通知され、それぞれのアクションを追跡します
        </p>
      ) : (
        <p className="text-xs text-[var(--accent-amber)] mt-1.5">
          未選択のときは全員宛になります（ステータス追跡・アクションボタンなし）
        </p>
      )}
    </div>
  );
}
